import React, { useState } from 'react'
import { currentLeadAttribution, submitContactRequest, type ContactFormPayload } from '../../services/contact'
import type { ContactLandingCopy } from './landingContent'

type ContactSectionProps = {
  copy: ContactLandingCopy
}

const initialForm: ContactFormPayload = {
  name: '',
  email: '',
  company: '',
  phone: '',
  message: '',
  contactWebsite: '',
}

export const ContactSection: React.FC<ContactSectionProps> = ({ copy }) => {
  const [form, setForm] = useState<ContactFormPayload>(initialForm)
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const updateField = (field: keyof ContactFormPayload) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((current) => ({ ...current, [field]: event.target.value }))
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setStatus('loading')
    setErrorMessage('')

    try {
      await submitContactRequest({ ...form, ...currentLeadAttribution() })
      setForm(initialForm)
      setStatus('success')
    } catch (error: any) {
      setStatus('error')
      setErrorMessage(error?.response?.data?.message || copy.error)
    }
  }

  return (
    <section id="contact" className="landing-section landing-contact">
      <div className="container landing-contact__grid">
        <div className="landing-section-heading">
          <p className="landing-eyebrow">{copy.eyebrow}</p>
          <h2>{copy.title}</h2>
          <p>{copy.copy}</p>
        </div>

        <form className="landing-contact__form" onSubmit={handleSubmit}>
          <label style={{ display: 'none' }} aria-hidden="true">
            Leave this field empty
            <input name="contactWebsite" value={form.contactWebsite} onChange={updateField('contactWebsite')} tabIndex={-1} autoComplete="off" />
          </label>
          <div className="landing-contact__row">
            <label>
              <span>{copy.fields.name}</span>
              <input
                className="input"
                name="name"
                placeholder={copy.fields.name}
                value={form.name}
                onChange={updateField('name')}
                required
                autoComplete="name"
              />
            </label>
            <label>
              <span>{copy.fields.email}</span>
              <input
                className="input"
                type="email"
                name="email"
                placeholder={copy.fields.email}
                value={form.email}
                onChange={updateField('email')}
                required
                autoComplete="email"
              />
            </label>
          </div>
          <div className="landing-contact__row">
            <label>
              <span>{copy.fields.company}</span>
              <input
                className="input"
                name="company"
                placeholder={copy.fields.company}
                value={form.company}
                onChange={updateField('company')}
                autoComplete="organization"
              />
            </label>
            <label>
              <span>{copy.fields.phone}</span>
              <input
                className="input"
                name="phone"
                placeholder={copy.fields.phone}
                value={form.phone}
                onChange={updateField('phone')}
                autoComplete="tel"
              />
            </label>
          </div>
          <label>
            <span>{copy.fields.message}</span>
            <textarea
              className="input"
              name="message"
              placeholder={copy.fields.message}
              value={form.message}
              onChange={updateField('message')}
              required
              rows={6}
            />
          </label>
          <button className="btn btn-primary" type="submit" disabled={status === 'loading'}>
            {status === 'loading' ? copy.submitting : copy.submit}
          </button>
          {status === 'success' && <p className="landing-contact__status landing-contact__status--success">{copy.success}</p>}
          {status === 'error' && <p className="landing-contact__status landing-contact__status--error">{errorMessage}</p>}
        </form>
      </div>
    </section>
  )
}
